import { CreateResearchDialog } from "@/components/DocumentCreate/CreateResearchDialog";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import {
  ResearchIcon,
  TemplateIcon,
} from "@/components/display/display-icon/artifacts";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Column, Row } from "@/components/ui/flex";
import { useDocumentsData } from "@/data/use-documents-data";
import { useKitData } from "@/data/use-kits-data";
import { useSettingData } from "@/data/use-settings-data";
import { useKitTemplatesData } from "@/data/use-templates-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { Template } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

export const ProjectPage = () => {
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { kit } = useKitData(groupUuid, kitUuid);
  const {
    documents,
    isLoading: isDocumentsLoading,
    createDocument,
  } = useDocumentsData(groupUuid, kitUuid);
  const [researchDialog, setResearchDialog] = useState<boolean>(false);
  const { templates } = useKitTemplatesData(groupUuid, kitUuid);
  const navigate = useNavigate();
  const { value: researcherFeatureFlag } = useSettingData(
    groupUuid,
    "FEATURE_FLAG_RESEARCHER",
  );

  const handleCreateBlankDocument = async () => {
    const document = await createDocument({});
    if (document) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid",
        params: {
          groupSlug,
          kitUuid: kitUuid!,
          documentUuid: document.uuid,
        },
      });
    }
  };

  const handleCreateFromTemplate = (template: Template) => async () => {
    const document = await createDocument({
      name: template.name,
      templateUuid: template.uuid,
    });
    if (document) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid",
        params: {
          groupSlug,
          kitUuid: kitUuid!,
          documentUuid: document.uuid,
        },
      });
    }
  };

  if (!kit || isDocumentsLoading) {
    return null;
  }

  if (documents.length > 0) {
    return (
      <Navigate
        to="/w/$groupSlug/k/$kitUuid/document/$documentUuid"
        params={{
          groupSlug,
          kitUuid: kitUuid!,
          documentUuid: documents[0].uuid,
        }}
        replace
      />
    );
  }

  return (
    <>
      <Page>
        <PageHeader />
        <PageBody innerClassName="items-center justify-center pb-24">
          <Column className="items-center gap-8">
            <div className="text-2xl font-bold">Let's get you started.</div>
            <Row className="gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <TemplateIcon /> Create Document
                    <FontAwesomeIcon icon={faChevronDown} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleCreateBlankDocument}>
                    Blank Document
                  </DropdownMenuItem>
                  {templates.map((template) => (
                    <DropdownMenuItem
                      onClick={handleCreateFromTemplate(template)}
                    >
                      {template.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {researcherFeatureFlag === "enabled" && (
                <Button onClick={() => setResearchDialog(true)}>
                  <ResearchIcon /> Create Research
                </Button>
              )}
            </Row>
          </Column>
        </PageBody>
      </Page>

      <CreateResearchDialog
        open={researchDialog}
        onOpenChange={() => setResearchDialog(false)}
      />
    </>
  );
};
